import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Ticket, RefreshCw, CalendarDays, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const typeLabel = (t) => ({
    straight: 'Straight', box: 'Box', straight_box: 'Str+Box',
    combo: 'Combo', trips: 'All Triples', combo_all: 'All Combos',
}[t] || t || '');

/**
 * LotteryDailyWinnersCard — "which lottery tickets won each day", grouped by
 * DRAW DATE (last 14 draws). Mirrors the sportsbook daily-winners view. Reads
 * GET /api/admin/reports/lottery.
 */
export default function LotteryDailyWinnersCard() {
    const [days, setDays] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openDay, setOpenDay] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/reports/lottery`, { withCredentials: true });
            const dw = data.daily_winners || [];
            setDays(dw);
            setOpenDay((prev) => prev || dw[0]?.date || null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load lottery winners');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="card-dark p-5 space-y-4" data-testid="lottery-winners-card">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                    <Ticket className="w-5 h-5 text-[#F59E0B]" />
                    <h3 className="text-sm uppercase font-bold tracking-wide text-white">Lottery — Winning Tickets by Day</h3>
                    <span className="text-[10px] text-[#A0AAB2]">grouped by draw date · last 14 draws</span>
                </div>
                <button onClick={load} disabled={loading}
                    className="text-xs text-[#A0AAB2] hover:text-white flex items-center gap-1"
                    data-testid="lottery-winners-refresh">
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {!days ? (
                <p className="text-[11px] text-[#A0AAB2] italic">Loading…</p>
            ) : days.length === 0 ? (
                <div className="rounded-lg border border-white/5 bg-[#0a0a0a] p-4 text-center text-[11px] text-[#A0AAB2] italic" data-testid="lottery-winners-empty">
                    No winning lottery tickets in the last 14 draws yet.
                </div>
            ) : (
                <div data-testid="lottery-winners-list">
                    <p className="text-[10px] uppercase tracking-widest text-[#A0AAB2] font-bold mb-2 flex items-center gap-1">
                        <CalendarDays className="w-3 h-3 text-[#39FF14]" />
                        Winning tickets by draw date (CT)
                    </p>
                    <div className="rounded-lg border border-white/5 bg-[#0a0a0a] divide-y divide-white/5 overflow-hidden">
                        {days.map((d) => {
                            const isOpen = openDay === d.date;
                            const label = new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                            return (
                                <div key={d.date} data-testid={`lottery-winners-day-${d.date}`}>
                                    <button type="button"
                                        onClick={() => setOpenDay(isOpen ? null : d.date)}
                                        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-white/[0.04] transition-colors"
                                        data-testid={`lottery-winners-toggle-${d.date}`}>
                                        <span className="flex items-center gap-2 min-w-0">
                                            <ChevronDown className={`w-3.5 h-3.5 text-[#A0AAB2] shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                                            <span className="text-xs font-bold text-white">{label}</span>
                                            <span className="text-[10px] text-[#39FF14] bg-[#39FF14]/10 px-1.5 py-0.5 rounded-full font-bold">{d.count} won</span>
                                        </span>
                                        <span className="text-[11px] font-mono text-[#A0AAB2] shrink-0">
                                            paid <span className="text-[#FFD700] font-bold">{fmt(d.total_paid)}</span>
                                        </span>
                                    </button>
                                    {isOpen && (
                                        <div className="bg-black/30 divide-y divide-white/5" data-testid={`lottery-winners-tickets-${d.date}`}>
                                            {d.tickets.map((t) => (
                                                <div key={t.id} className="flex items-center justify-between px-3 py-2 pl-8 text-[11px]" data-testid={`lottery-winner-${t.id}`}>
                                                    <div className="min-w-0 pr-2">
                                                        <p className="text-white truncate">
                                                            {t.name}
                                                            <span className="text-[#666]"> · {t.state} Pick {t.pick_type}</span>
                                                        </p>
                                                        <p className="text-[#A0AAB2] truncate flex items-center gap-1">
                                                            {t.numbers ? (
                                                                <span className="inline-flex gap-0.5">
                                                                    {t.numbers.map((n, j) => (
                                                                        <span key={j} className="w-4 h-4 rounded bg-[#FFD700]/15 text-[#FFD700] text-[9px] font-bold flex items-center justify-center">{n}</span>
                                                                    ))}
                                                                </span>
                                                            ) : <span>{t.numbers_label}</span>}
                                                            <span className="text-[#666]"> · {typeLabel(t.bet_type)}</span>
                                                            <span className={`text-[9px] px-1 rounded font-black uppercase ${(t.drawing_time || '').toLowerCase() === 'evening' ? 'bg-[#1E3A8A]/40 text-[#93C5FD]' : 'bg-[#FFD700]/15 text-[#FFD700]'}`}>
                                                                {(t.drawing_time || '').toLowerCase() === 'evening' ? 'Eve' : 'Mid'}
                                                            </span>
                                                        </p>
                                                    </div>
                                                    <div className="text-right shrink-0 font-mono">
                                                        <p className="text-[#A0AAB2]">{fmt(t.stake)} → <span className="text-[#39FF14] font-bold">{fmt(t.payout)}</span></p>
                                                        <p className="text-[#39FF14]">+{fmt(t.profit)} profit</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
